import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, TextField, Button, CircularProgress, Alert } from "@mui/material";
import REVIEWABLE_MODULES, { moduleByKey, REVIEWABLE_META_FIELDS, backendUrl } from "./reviewableModules";
import "../IncidentReports/DailySituationReport.css";
import "./ReportList.css";

// Same auth/role lookup pattern used by ProtectedRoute.js and Sidebar.js -
// role/is_superuser are cached in localStorage at login, not re-fetched here.
const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};
const authHeadersJson = () => ({ "Content-Type": "application/json", ...authHeaders() });
const isSuperAdmin = () => {
    const role = localStorage.getItem("role");
    return role === "superadmin" || localStorage.getItem("is_superuser") === "true";
};

const humanize = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const resolveLocationParts = (mod, report, locationNames) => {
    const [muniField, subField, sucoField] = mod.locationFields;
    return {
        municipality: locationNames.municipality.get(report[muniField]) || report[muniField] || "",
        subMunicipality: locationNames.subMunicipality.get(report[subField]) || report[subField] || "",
        suco: locationNames.suco.get(report[sucoField]) || report[sucoField] || "",
    };
};

// Every field name any module ever uses for location/event-date - each of
// those already has its own dedicated Municipality/Suco/Date column, so
// they're left out of the "every remaining field" column set below.
const ALL_LOCATION_FIELD_NAMES = new Set(REVIEWABLE_MODULES.flatMap((m) => m.locationFields));
const ALL_DATE_FIELD_NAMES = new Set(REVIEWABLE_MODULES.map((m) => m.dateField));
const EXCLUDED_DYNAMIC_COLUMNS = new Set([
    ...REVIEWABLE_META_FIELDS, "_moduleKey", "id", "report_id", "submitted_at", "advisory",
    ...ALL_LOCATION_FIELD_NAMES, ...ALL_DATE_FIELD_NAMES,
]);
const COLUMN_LABEL_OVERRIDES = { geojson_feature: "Area / Polygon" };
const columnLabel = (key) => COLUMN_LABEL_OVERRIDES[key] || humanize(key);
const formatCellValue = (column, value) => {
    if (value === null || value === undefined || value === "") return "";
    if (column === "geojson_feature") return value ? "Polygon" : "";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

// Column order per module, matching the order each field is actually asked
// for on that module's own report form - not alphabetical, so this table
// reads the same way the report was originally filled in. Anything a
// module's serializer returns that isn't listed here (a field this list
// wasn't updated for) still shows up, just appended after the known ones.
const MODULE_FIELD_ORDER = {
    cyclone: [
        "location", "latitude", "longitude", "starting_point_lat", "starting_point_long", "ending_point_lat", "ending_point_long",
        "time_cyclone", "time_type", "wind_speed", "rainfall_amount", "storm_surge_height", "damage_type", "warning_received", "affected_status",
        "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount", "additional_info",
    ],
    flood: [
        "location", "latitude", "longitude", "starting_point_lat", "starting_point_long", "ending_point_lat", "ending_point_long",
        "time_flood", "time_type", "water_level", "flood_duration", "damage_type", "apparent_cause", "affected_status",
        "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount", "additional_info",
    ],
    landslide: [
        "location", "latitude", "longitude", "starting_point_lat", "starting_point_long", "ending_point_lat", "ending_point_long",
        "time_landslide", "time_type", "landslide_type", "width", "length", "approximate_size", "materials",
        "was_raining", "apparent_cause", "rainfall_description", "previous_landslides", "warning_signs", "warning_signs_description",
        "damage_caused", "affected_status", "damage_description",
        "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount", "additional_info",
    ],
    earthquake: [
        "felt_it", "location", "latitude", "longitude", "situation", "building_structure", "ground_shaking",
        "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount", "message",
    ],
    lightning: [
        "location", "latitude", "longitude", "starting_point_lat", "starting_point_long", "ending_point_lat", "ending_point_long",
        "time_lightning", "time_type", "effect_type", "strike", "affected_status",
        "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount", "additional_info",
    ],
    firerisk: [
        "location", "latitude", "longitude", "starting_point_lat", "starting_point_long", "ending_point_lat", "ending_point_long",
        "time_fire", "time_type", "fire_size", "vegetation_type", "cause_of_fire", "affected_status", "apparent_cause_description",
        "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount", "additional_info",
    ],
    road_accident: [
        "location", "latitude", "longitude", "accident_type", "motorized_vehicle1", "motorized_vehicle2", "motorized_vehicle", "others",
        "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount", "message",
    ],
    road_condition: ["location", "latitude", "longitude", "issue", "other_issue", "message"],
    black_spot: ["year", "roadtype", "location", "latitude", "longitude", "numberofaccidents", "numberofinjured", "numberoffatal"],
};
const columnRank = (moduleKey, key) => {
    const order = MODULE_FIELD_ORDER[moduleKey] || [];
    const idx = order.indexOf(key);
    return idx === -1 ? order.length + 1000 : idx;
};

// The integer loss/damage fields - Black Spot names its two equivalents
// differently (numberofinjured/numberoffatal), everything else shares the
// same 5 field names (see each felt-report model's "Loss/damage estimates"
// fields). These are the only cells that become inline-editable inputs.
const LOSS_DAMAGE_EDITABLE_KEYS = new Set([
    "people_injured_count", "people_deaths_count", "houses_affected_count", "livestock_lost_count", "estimated_loss_amount",
    "numberofinjured", "numberoffatal",
]);

// date input value (YYYY-MM-DD), in the browser's local time - no
// timezone conversion surprises (new Date().toISOString() would shift to UTC).
const toLocalDateValue = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const defaultToDate = () => new Date();
const defaultFromDate = () => new Date(Date.now() - 24 * 60 * 60 * 1000); // 24-hour default window

// Native <input type="date"> only opens its calendar popup when the small
// browser-drawn icon itself is clicked - this makes a click anywhere in the
// field do the same, via the (Chrome/Edge) showPicker() API. No-ops quietly
// on browsers that don't support it (Safari/Firefox), which fall back to
// their own default click-to-focus behavior.
const openDatePicker = (event) => {
    if (typeof event.target.showPicker === "function") {
        try { event.target.showPicker(); } catch (error) { /* ignore - e.g. not user-initiated */ }
    }
};

const ReportList = () => {
    const superAdmin = isSuperAdmin();

    const [fromDate, setFromDate] = useState(toLocalDateValue(defaultFromDate()));
    const [toDate, setToDate] = useState(toLocalDateValue(defaultToDate()));
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeModuleKey, setActiveModuleKey] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [locationNames, setLocationNames] = useState({
        municipality: new Map(), subMunicipality: new Map(), suco: new Map(),
    });

    // Edits staged in the table's own inline inputs, not yet sent - keyed by
    // report id, cleared whenever the active module tab changes so edits
    // never silently carry over onto a different module's rows.
    const [stagedEdits, setStagedEdits] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [muniRes, subRes, sucoRes] = await Promise.all([
                    fetch(`${backendUrl}/Municipalities/municipalities_list/`, { method: "POST", headers: authHeaders() }),
                    fetch(`${backendUrl}/Municipalities/sub_municipalities_list/`, { method: "POST", headers: authHeaders() }),
                    fetch(`${backendUrl}/Municipalities/suco_master_list/`, { headers: authHeaders() }),
                ]);
                const [munis, subs, sucos] = await Promise.all([muniRes.json(), subRes.json(), sucoRes.json()]);
                setLocationNames({
                    municipality: new Map((Array.isArray(munis) ? munis : []).map((m) => [m.adm1_code, m.adm1])),
                    subMunicipality: new Map((Array.isArray(subs) ? subs : []).map((s) => [s.adm2_code, s.adm2])),
                    suco: new Map((Array.isArray(sucos) ? sucos : []).map((s) => [s.adm3_code, s.adm3])),
                });
            } catch (error) {
                console.error("Error loading municipality/suco master lists:", error);
            }
        })();
    }, []);

    // Verified-and-active reports only - a merged (duplicate) report never
    // carries verification_status "verified" (apply_merge/apply_edit always
    // flip it to "merged" the moment it's folded into another report), so
    // filtering on status alone already excludes superseded/duplicate rows.
    // Always fetches every module - which ones get a visible tab is decided
    // afterward, from whatever actually has data in the date range.
    const fetchReports = useCallback(async () => {
        setLoading(true);
        try {
            const results = await Promise.all(
                REVIEWABLE_MODULES.map(async (m) => {
                    try {
                        const response = await fetch(m.base, { headers: authHeaders() });
                        const data = await response.json();
                        if (!response.ok) throw new Error((data && data.error) || `Failed to load ${m.label} reports`);
                        const rows = Array.isArray(data) ? data : (data.reports || []);
                        return rows
                            .filter((r) => r.verification_status === "verified")
                            .map((r) => ({ ...r, _moduleKey: m.key }));
                    } catch (error) {
                        console.error(`Error fetching ${m.label} reports:`, error);
                        setSnackbar({ open: true, message: error.message || `Could not load ${m.label} reports.`, severity: "error" });
                        return [];
                    }
                })
            );
            setReports(results.flat());
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchReports(); }, [fetchReports]);

    useEffect(() => {
        if (!snackbar.open) return;
        const timer = setTimeout(() => setSnackbar((p) => ({ ...p, open: false })), 6000);
        return () => clearTimeout(timer);
    }, [snackbar.open]);

    // Reports without a parseable event date are kept (can't tell whether
    // they belong in the window or not) - everything else is filtered to
    // the From/To range on its own module's event-date field.
    const visibleReports = useMemo(() => {
        // "To" is a whole calendar day, not a timestamp - stretch it to that
        // day's last instant so a report dated anytime on the "To" day
        // still counts as within range instead of only its midnight.
        const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
        const to = toDate ? new Date(`${toDate}T23:59:59.999`) : null;
        return reports.filter((r) => {
            const mod = moduleByKey(r._moduleKey);
            const raw = mod ? r[mod.dateField] : null;
            if (!raw) return true;
            const d = new Date(raw);
            if (Number.isNaN(d.getTime())) return true;
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        });
    }, [reports, fromDate, toDate]);

    // Only modules that actually have a verified report in the current date
    // range get a tab - an empty category (e.g. nothing verified yet for
    // Flood) doesn't clutter the tab row with nothing behind it.
    const modulesWithData = useMemo(
        () => REVIEWABLE_MODULES.filter((m) => visibleReports.some((r) => r._moduleKey === m.key)),
        [visibleReports]
    );

    // Keeps the active tab valid as the date range changes - falls back to
    // the first module that still has data, or clears out if none do.
    useEffect(() => {
        if (modulesWithData.length === 0) { setActiveModuleKey(null); return; }
        if (!modulesWithData.some((m) => m.key === activeModuleKey)) {
            setActiveModuleKey(modulesWithData[0].key);
        }
    }, [modulesWithData, activeModuleKey]);

    // Any staged edits belong to the module tab they were made on - switch
    // tabs and they're dropped rather than silently applying to a different
    // module's (unrelated) reports.
    useEffect(() => { setStagedEdits({}); }, [activeModuleKey]);

    const activeMod = activeModuleKey ? moduleByKey(activeModuleKey) : null;
    const moduleReports = useMemo(
        () => (activeModuleKey ? visibleReports.filter((r) => r._moduleKey === activeModuleKey) : []),
        [visibleReports, activeModuleKey]
    );

    // Union of every field name on the active module's own rows, minus the
    // ones that already have a dedicated column, ordered to match that
    // module's own report form instead of alphabetically.
    const dynamicColumns = useMemo(() => {
        const keys = new Set();
        moduleReports.forEach((r) => {
            Object.keys(r).forEach((k) => {
                if (!EXCLUDED_DYNAMIC_COLUMNS.has(k)) keys.add(k);
            });
        });
        return Array.from(keys).sort((a, b) => {
            const rankDiff = columnRank(activeModuleKey, a) - columnRank(activeModuleKey, b);
            return rankDiff !== 0 ? rankDiff : columnLabel(a).localeCompare(columnLabel(b));
        });
    }, [moduleReports, activeModuleKey]);

    const stagedCount = Object.keys(stagedEdits).length;

    const handleCellEdit = (reportId, field, value) => {
        setStagedEdits((prev) => ({ ...prev, [reportId]: { ...prev[reportId], [field]: value } }));
    };

    const handleSaveChanges = async () => {
        if (!activeMod || stagedCount === 0) return;
        setSaving(true);
        let successCount = 0;
        let failCount = 0;
        try {
            await Promise.all(
                Object.entries(stagedEdits).map(async ([reportId, fields]) => {
                    const payload = {};
                    Object.entries(fields).forEach(([key, value]) => { if (value !== "") payload[key] = value; });
                    if (Object.keys(payload).length === 0) return;
                    try {
                        const response = await fetch(`${activeMod.base}${reportId}/edit/`, {
                            method: "POST", headers: authHeadersJson(), body: JSON.stringify(payload),
                        });
                        const data = await response.json().catch(() => ({}));
                        if (!response.ok) throw new Error(data.error || `Failed to save report #${reportId}`);
                        successCount += 1;
                    } catch (error) {
                        console.error(`Error saving report #${reportId}:`, error);
                        failCount += 1;
                    }
                })
            );
            setSnackbar({
                open: true,
                severity: failCount === 0 ? "success" : "error",
                message: failCount === 0
                    ? `Saved changes to ${successCount} report${successCount === 1 ? "" : "s"}.`
                    : `Saved ${successCount}, failed ${failCount} - check console for details.`,
            });
            setStagedEdits({});
            fetchReports();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box className="dsr-container">
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: "18px", mb: 0.5 }}>
                            Report List — Verified &amp; Active
                        </Typography>
                        {!superAdmin && (
                            <Typography sx={{ fontSize: "12.5px", color: "#94a3b8" }}>
                                View only — editing this list is restricted to Super Admin.
                            </Typography>
                        )}
                    </Box>
                    {superAdmin && (
                        <Button
                            variant="contained" disabled={stagedCount === 0 || saving} onClick={handleSaveChanges}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                            {saving ? "Saving…" : stagedCount > 0 ? `Save Changes (${stagedCount})` : "Save Changes"}
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mt: 2 }}>
                    <TextField
                        label="From" type="date" size="small" value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 170, "& input[type='date']::-webkit-calendar-picker-indicator": { cursor: "pointer" } }}
                        inputProps={{ onClick: openDatePicker, style: { cursor: "pointer" } }}
                    />
                    <TextField
                        label="To" type="date" size="small" value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 170, "& input[type='date']::-webkit-calendar-picker-indicator": { cursor: "pointer" } }}
                        inputProps={{ onClick: openDatePicker, style: { cursor: "pointer" } }}
                    />
                    <Button
                        size="small" onClick={() => { setFromDate(toLocalDateValue(defaultFromDate())); setToDate(toLocalDateValue(defaultToDate())); }}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Reset to last 24 hours
                    </Button>
                </Box>

                {modulesWithData.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2.5 }}>
                        {modulesWithData.map((m) => {
                            const count = visibleReports.filter((r) => r._moduleKey === m.key).length;
                            const active = activeModuleKey === m.key;
                            return (
                                <Button
                                    key={m.key} size="small" onClick={() => setActiveModuleKey(m.key)}
                                    sx={{
                                        textTransform: "none", fontWeight: 700, borderRadius: "8px", px: 1.75,
                                        backgroundColor: active ? "#2563eb" : "#f1f5f9",
                                        color: active ? "#fff" : "#475569",
                                        "&:hover": { backgroundColor: active ? "#1d4ed8" : "#e2e8f0" },
                                    }}
                                >
                                    {m.label} ({count})
                                </Button>
                            );
                        })}
                    </Box>
                )}
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <Box className="report-list-table-scroll">
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>
                    ) : !activeMod ? (
                        <Box sx={{ p: 3, color: "#94a3b8", fontSize: "14px" }}>No verified reports in this date range.</Box>
                    ) : (
                        <table className="report-list-table">
                            <thead>
                                <tr>
                                    <th>S.No</th><th>Municipality</th><th>Sub Municipality</th><th>Suco</th><th>Date</th><th>Advisory</th>
                                    {dynamicColumns.map((col) => <th key={col}>{columnLabel(col)}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {moduleReports.map((report, index) => {
                                    const loc = resolveLocationParts(activeMod, report, locationNames);
                                    return (
                                        <tr key={report.id}>
                                            <td>{index + 1}</td>
                                            <td>{loc.municipality || ""}</td>
                                            <td>{loc.subMunicipality || ""}</td>
                                            <td>{loc.suco || ""}</td>
                                            <td>{report[activeMod.dateField] || ""}</td>
                                            <td>{report.advisory || ""}</td>
                                            {dynamicColumns.map((col) => {
                                                if (superAdmin && LOSS_DAMAGE_EDITABLE_KEYS.has(col)) {
                                                    const staged = stagedEdits[report.id]?.[col];
                                                    const isDirty = staged !== undefined;
                                                    return (
                                                        <td key={col} className={`report-list-input-cell${isDirty ? " report-list-dirty" : ""}`}>
                                                            <input
                                                                type="number"
                                                                className="report-list-cell-input"
                                                                value={staged ?? (report[col] ?? "")}
                                                                onChange={(e) => handleCellEdit(report.id, col, e.target.value)}
                                                            />
                                                        </td>
                                                    );
                                                }
                                                return <td key={col}>{formatCellValue(col, report[col])}</td>;
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </Box>
            </Paper>

            {snackbar.open && (
                <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1400 }}>
                    <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} variant="filled" sx={{ minWidth: "300px" }}>
                        {snackbar.message}
                    </Alert>
                </Box>
            )}
        </Box>
    );
};

export default ReportList;
